import type { UaProductDbClient } from "./pages";

/* ── Types ──────────────────────────────────────────────────── */

export type UaCallSettings = {
  voiceProvider: "twilio" | "telnyx";
  dialFromPhoneNumbers: string[];
  maxActiveCallsPerDialFromNumber: number;
  dialToPhoneNumber: string;
  maxCallDurationSeconds: number;
  webhookStatusCallbackBaseUrl: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  telnyxApiKey: string;
  telnyxPublicKey: string;
  telnyxConnectionId: string;
};

export type ActiveCallEntry = {
  callSid: string;
  dbCall: ActiveDbCall | null;
  providerActive: boolean;
};

export type ActiveDbCall = {
  id: number;
  userId: number;
  callSid: string;
  status: string;
  startedAt: string;
  voiceProvider: string | null;
  errorReason: string | null;
};

export type CallHistoryEntry = {
  id: number;
  userId: number;
  callSid: string;
  status: string;
  resultLabel: string | null;
  resultDate: string | null;
  transcript: string | null;
  recordingAvailable: boolean;
  voiceProvider: string | null;
  errorReason: string | null;
  startedAt: string;
};

/* ── Admin API helpers ──────────────────────────────────────── */

type RequestAdmin = NonNullable<UaProductDbClient["requestAdmin"]>;

export async function loadUaCallSettings(
  requestAdmin: RequestAdmin
): Promise<{ ok: true; settings: UaCallSettings } | { ok: false; error: string }> {
  const result = await requestAdmin<{ settings: UaCallSettings }>({
    method: "GET",
    path: "/api/admin/co-ua/runtime-settings",
  });
  if (!result.ok) return { ok: false, error: result.error };
  return { ok: true, settings: result.data.settings };
}

export async function saveUaCallSettings(
  settings: UaCallSettings,
  requestAdmin: RequestAdmin
): Promise<{ ok: true } | { ok: false; error: string }> {
  const result = await requestAdmin({
    method: "POST",
    path: "/api/admin/co-ua/runtime-settings",
    body: settings,
  });
  return result.ok ? { ok: true } : { ok: false, error: result.error };
}

export async function loadUaActiveCalls(
  requestAdmin: RequestAdmin
): Promise<
  | { ok: true; entries: ActiveCallEntry[]; providerError: string | null }
  | { ok: false; error: string }
> {
  const [providerResult, dbResult] = await Promise.all([
    requestAdmin<{ callSids?: string[] }>({
      method: "GET",
      path: "/api/admin/co-ua/active-calls",
    }),
    requestAdmin<{ calls?: ActiveDbCall[] }>({
      method: "GET",
      path: "/api/admin/co-ua/active-db-calls",
    }),
  ]);

  if (!dbResult.ok) {
    return { ok: false, error: dbResult.error };
  }

  const providerError = providerResult.ok ? null : providerResult.error;
  const providerSids = new Set(
    providerResult.ok && Array.isArray(providerResult.data.callSids)
      ? providerResult.data.callSids
      : []
  );
  const dbCalls = dbResult.ok && Array.isArray(dbResult.data.calls) ? dbResult.data.calls : [];
  const dbCallsBySid = new Map<string, ActiveDbCall>();
  for (const call of dbCalls) {
    dbCallsBySid.set(call.callSid, call);
  }

  const allSids = new Set([...providerSids, ...dbCallsBySid.keys()]);
  const entries: ActiveCallEntry[] = [];
  for (const callSid of allSids) {
    entries.push({
      callSid,
      dbCall: dbCallsBySid.get(callSid) ?? null,
      providerActive: providerSids.has(callSid),
    });
  }
  entries.sort((a, b) => {
    const aTime = a.dbCall?.startedAt ?? "";
    const bTime = b.dbCall?.startedAt ?? "";
    return bTime > aTime ? 1 : bTime < aTime ? -1 : 0;
  });

  return { ok: true, entries, providerError };
}

export async function requestUaManualHangup(
  callSid: string,
  requestAdmin: RequestAdmin
): Promise<{ ok: true; result: { callSid: string } } | { ok: false; error: string }> {
  const apiResult = await requestAdmin<{ result: { callSid: string } }>({
    method: "POST",
    path: "/api/admin/co-ua/hangup-call",
    body: { callSid },
  });
  if (!apiResult.ok) return { ok: false, error: apiResult.error };
  return { ok: true, result: apiResult.data.result };
}

export async function resolveUaStaleCallFromAdmin(
  callId: number,
  resolution: "completed" | "failed",
  requestAdmin: RequestAdmin
): Promise<{ ok: true } | { ok: false; error: string }> {
  const result = await requestAdmin({
    method: "POST",
    path: "/api/admin/co-ua/resolve-call",
    body: { callId, resolution },
  });
  return result.ok ? { ok: true } : { ok: false, error: result.error };
}

export async function loadUaCallHistory(
  requestAdmin: RequestAdmin,
  userId?: number
): Promise<{ ok: true; calls: CallHistoryEntry[] } | { ok: false; error: string }> {
  const path = userId
    ? `/api/admin/co-ua/call-history?userId=${userId}`
    : "/api/admin/co-ua/call-history";
  const result = await requestAdmin<{ calls: CallHistoryEntry[] }>({
    method: "GET",
    path,
  });
  if (!result.ok) return { ok: false, error: result.error };
  return { ok: true, calls: result.data.calls };
}
